import { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  alpha,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import SpeedIcon from "@mui/icons-material/Speed";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import BuildIcon from "@mui/icons-material/Build";
import TerrainIcon from "@mui/icons-material/Terrain";
import type { Alert } from "../data/pipelineData";

interface AlertsPanelProps {
  alerts: Alert[];
  onAlertClick?: (alert: Alert) => void;
}

const severityConfig = {
  critical: { color: "error", label: "Critical", priority: 0 },
  high: { color: "warning", label: "High", priority: 1 },
  medium: { color: "info", label: "Medium", priority: 2 },
  low: { color: "success", label: "Low", priority: 3 },
} as const;

const typeIcons: Record<Alert["type"], React.ReactNode> = {
  leak: <WaterDropIcon fontSize="small" />,
  pressure: <SpeedIcon fontSize="small" />,
  intrusion: <PersonOffIcon fontSize="small" />,
  equipment: <BuildIcon fontSize="small" />,
  environmental: <TerrainIcon fontSize="small" />,
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

interface AlertItemProps {
  alert: Alert;
  onClick?: () => void;
}

const AlertItem = ({ alert, onClick }: AlertItemProps) => {
  const severity = severityConfig[alert.severity];

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.5,
        py: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        borderLeft: "3px solid",
        borderLeftColor: `${severity.color}.main`,
        "&:hover": onClick
          ? {
              bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08),
            }
          : {},
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box
          sx={{
            color: `${severity.color}.main`,
            mt: 0.25,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {typeIcons[alert.type]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.5}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ flex: 1 }}
            >
              {alert.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              {formatTimestamp(alert.timestamp)}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {alert.description}
          </Typography>
        </Box>
        {onClick && (
          <Tooltip title="Go to location" arrow placement="left">
            <GpsFixedIcon
              fontSize="small"
              sx={{
                color: "text.secondary",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

export const AlertsPanel = ({ alerts, onAlertClick }: AlertsPanelProps) => {
  const [expanded, setExpanded] = useState(true);

  const activeAlerts = useMemo(
    () => alerts.filter((a) => a.isActive),
    [alerts]
  );

  const sortedAlerts = useMemo(
    () =>
      [...activeAlerts].sort(
        (a, b) =>
          severityConfig[a.severity].priority -
          severityConfig[b.severity].priority
      ),
    [activeAlerts]
  );

  const alertCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    activeAlerts.forEach((alert) => {
      counts[alert.severity]++;
    });
    return counts;
  }, [activeAlerts]);

  const hasCritical = alertCounts.critical > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: 320,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
        backdropFilter: "blur(12px)",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
          bgcolor: hasCritical
            ? (theme) => alpha(theme.palette.error.main, 0.08)
            : "transparent",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberIcon
            fontSize="small"
            sx={{
              color: hasCritical ? "error.main" : "warning.main",
              animation: hasCritical ? "pulse 1.5s infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
          <Typography variant="subtitle2" fontWeight={600}>
            Active Alerts
          </Typography>
          <Chip
            label={activeAlerts.length}
            size="small"
            color={hasCritical ? "error" : "warning"}
          />
        </Stack>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Summary chips - always visible */}
      <Box sx={{ px: 1.5, py: 0.75, overflowX: "auto" }}>
        <Stack direction="row" spacing={0.5} flexWrap="nowrap">
          {(
            Object.entries(alertCounts) as [keyof typeof alertCounts, number][]
          ).map(
            ([severity, count]) =>
              count > 0 && (
                <Chip
                  key={severity}
                  label={`${count} ${severityConfig[severity].label}`}
                  size="small"
                  color={severityConfig[severity].color}
                  variant="outlined"
                />
              )
          )}
        </Stack>
      </Box>

      {/* Alert list */}
      <Collapse in={expanded}>
        <Divider />
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Stack divider={<Divider />}>
            {sortedAlerts.map((alert) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                onClick={onAlertClick ? () => onAlertClick(alert) : undefined}
              />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};
